/**
 * Recurrence schema (Plan 02-01 Task 2 / D-03).
 *
 * Single Zod source of truth for the `chores.recurrence` JSON column. Every
 * write to `chores` validates against `Recurrence` before INSERT — and the
 * recurrence-engine cron (`spawnRecurringChores`) re-parses on every tick so
 * malformed legacy rows fail loud rather than silently spawning the wrong
 * cadence.
 *
 * Variants:
 *   { type: 'once' }                              — single instance, never re-spawns
 *   { type: 'daily',  interval? }                 — every `interval`-th Oslo day (default 1)
 *   { type: 'weekdays', days: [1..7] }            — only on the listed ISO weekdays (Mon=1)
 *   { type: 'weekly',   weekday: 1..7, interval? }— weekday N every `interval`-th week (default 1)
 *   { type: 'monthly',  dayOfMonth: 1..31 }       — one instance per month on day-of-month
 *
 * `interval` (Fase 2) expresses "annenhver dag/uke" cadences: interval 2 =
 * every other period, 3 = every third, etc. Omitted/1 = every period (the
 * pre-Fase-2 behaviour). The interval is counted from the template's
 * created_at anchor by `recurrenceMatches` — the schema only validates the
 * shape; it carries no anchor itself.
 *
 * `discriminatedUnion` makes the inferred TS type a sum type — switch
 * statements over `r.type` get exhaustiveness checking for free.
 *
 * The keyword "type" is reserved for the discriminator. No other field can
 * be named `type`. Days array length is bounded 1..7 (zero days = "weekly
 * never" which is `once`; eight values would duplicate Mon). `interval` is
 * bounded 1..30 — a sane upper limit for a household chore cadence.
 */
import { z } from 'zod';

const interval = z.number().int().min(1).max(30).optional();

export const Recurrence = z.discriminatedUnion('type', [
  z.object({ type: z.literal('once') }),
  z.object({ type: z.literal('daily'), interval }),
  z.object({
    type: z.literal('weekdays'),
    days: z.array(z.number().int().min(1).max(7)).min(1).max(7),
  }),
  z.object({
    type: z.literal('weekly'),
    weekday: z.number().int().min(1).max(7),
    interval,
  }),
  z.object({
    type: z.literal('monthly'),
    dayOfMonth: z.number().int().min(1).max(31),
  }),
]);

export type Recurrence = z.infer<typeof Recurrence>;
